
import { Vue, Options } from 'vue-class-component'
import { maska } from 'maska'
import Form from '@/api/form'
import { PREFECTURES } from '@/utils/constants'
import { IDynamicLiffFormDetail, IDynamicLiffFormField } from '@/utils/types'
import FormPreview from '@/components/common/ui/FormPreview.vue'

declare global {
  interface Window {
    liff: any
  }
}

@Options({
  components: { FormPreview },
  directives: { maska },
})
export default class LIFFFormApp extends Vue {
  loading = true
  submitting = false
  formSubmitted = false
  formDetail: IDynamicLiffFormDetail | null = null
  formData: Record<string, any> = {}
  profile = null

  get prefectureOptions() {
    return PREFECTURES
  }

  // get liffId() {
  //   const path = this.$route.path
  //   const matches = path.match(/\/liff_form_app\/(.+)$/)
  //   return matches ? matches[1] : ''
  // }

  get richFormId() {
    return this.$route.params.rich_form_id as string
  }

  get isTestMode() {
    return this.$route.params.is_test_mode === 'true'
  }

  // Check if a field is valid
  isFieldValid(field: IDynamicLiffFormField): boolean {
    if (!field.is_required) return true

    const value = this.formData[field._id]

    switch (field.option_type) {
      case 'radio':
        return !!(value && value !== '')
      case 'checkbox':
        return !!(value && value.length > 0)
      default:
        return !!value
    }
  }

  // Validate form fields (radio and checkbox)
  validateFormFields(): string | null {
    if (!this.formDetail?.liff_details) return null

    for (const field of this.formDetail.liff_details) {
      if (!this.isFieldValid(field)) {
        return this.$t('validate.this_field_is_required', {
          placeholder: field.field_name,
        })
      }
    }

    return null
  }

  // Generate submission text from form data
  generateSubmissionText(): string {
    let text = `📩 ${this.formDetail?.title || 'Form'} Submission:\n\n`

    if (!this.formDetail?.liff_details) return text

    this.formDetail.liff_details.forEach((field) => {
      const value = this.formData[field._id]

      if (value !== '' && value !== false && value !== null && value !== undefined) {
        // Handle different field types for display
        let displayValue = value
        if (field.option_type === 'checkbox' && Array.isArray(value)) {
          displayValue = value.length > 0 ? value.join(', ') : ''
        }

        if (displayValue !== '') {
          text += `${field.field_name}: ${displayValue}\n`
        }
      }
    })

    return text
  }

  // Generate submission text from form data
  async submitForm() {
    // let text = `📩 ${this.formDetail?.title || 'Form'} Submission:\n\n`
    if (!this.formDetail?.liff_details) return

    const submission: IDynamicLiffFormDetail = {
      _id: this.formDetail?._id || '',
      app_id: this.formDetail?.app_id || '',
      liff_id: this.formDetail?.liff_id || '',
      form_size: this.formDetail.form_size,
      type: this.formDetail.type,
      title: this.formDetail.title,
      liff_details: this.formDetail.liff_details,
      response_details: this.formData,
      user_id: '', // Optionally, you can set the user ID if available
      is_test_mode: this.isTestMode,
    }
    submission.user_id = this.profile?.['userId'] ?? ''

    // console.log('Submitting form:', submission)

    await Form.submitFormPublic(submission)
    // console.log('Form submission response:', response)
  }

  async loadFormDetail() {
    try {
      this.loading = true
      if (!this.richFormId) {
        throw new Error('LIFF ID not found in URL')
      }

      const response = await Form.singlePublic(this.richFormId)
      this.formDetail = response

      this.initializeFormData()
    } catch (error) {
      console.error('Failed to load form detail:', error)
      this.$q.notify({
        type: 'negative',
        message: 'Failed to load form. Please try again.',
        position: 'top',
      })
    } finally {
      this.loading = false
    }
  }

  initializeFormData() {
    if (!this.formDetail?.liff_details) {
      this.formData = {}
      return
    }

    // Initialize form data with default values based on field types
    this.formData = this.formDetail.liff_details.reduce((data, field) => {
      switch (field.option_type) {
        case 'checkbox':
          data[field._id] = []
          break
        case 'select':
        case 'radio':
        case 'prefecture':
          data[field._id] = ''
          break
        default:
          data[field._id] = ''
      }
      return data
    }, {} as Record<string, any>)
  }

  closeLIFFWindow() {
    if (!window.liff.isInClient()) {
      this.$q.notify({
        type: 'negative',
        message: 'This button is unavailable as LIFF is currently being opened in an external browser.',
        position: 'top',
      })
    } else {
      window.liff.closeWindow()
    }
  }

  async onSubmit(submittedData: Record<string, any>) {
    try {
      this.submitting = true
      this.formSubmitted = true

      // Update form data with submitted data
      this.formData = submittedData
      // Custom validation for radio and checkbox required fields
      const validationError = this.validateFormFields()
      if (validationError) {
        this.$q.notify({
          type: 'negative',
          message: validationError,
          position: 'top',
        })
        return
      }

      // Create submission text
      const text = this.generateSubmissionText()

      this.submitForm()
      console.log('Message sent')

      if (window.liff && window.liff.isInClient()) {
        await window.liff.sendMessages([
          {
            type: 'text',
            text,
          },
        ])
      }

      this.$q.notify({
        type: 'positive',
        message: 'Form submitted successfully!',
        position: 'top',
      })

      // Close LIFF window
      this.closeLIFFWindow()
    } catch (error) {
      console.error('Submit error:', error)
      this.$q.notify({
        type: 'negative',
        message: 'Failed to submit form. Please try again.',
        position: 'top',
      })
    } finally {
      this.submitting = false
    }
  }

  onReset() {
    this.initializeFormData()
    this.formSubmitted = false
    this.closeLIFFWindow()
  }

  loadLiffSdk(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.liff) {
        return resolve()
      }

      const script = document.createElement('script')
      script.src = 'https://static.line-scdn.net/liff/edge/2/sdk.js'
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load LIFF SDK'))
      document.head.appendChild(script)
    })
  }

  async created() {
    try {
      await this.loadFormDetail()

      await this.loadLiffSdk()

      const liffIdToInit = this.formDetail?.liff_id || this.richFormId
      if (liffIdToInit) {
        await window.liff.init({
          liffId: liffIdToInit,
          // withLoginOnExternalBrowser: true,
        })
      }
      // Optional: Auto-fill form with user profile
      if (window.liff.isLoggedIn()) {
        this.profile = await window.liff.getProfile()
        console.log(this.profile, 'this.profile')

        // // Auto-fill name field if exists
        // if (this.formData.hasOwnProperty('Full Name') || this.formData.hasOwnProperty('Name')) {
        //   const nameField = this.formData['Full Name'] !== undefined ? 'Full Name' : 'Name'
        //   this.formData[nameField] = profile.displayName
        // }
      }
    } catch (err) {
      console.error('Initialization error:', err)
      this.$q.notify({
        type: 'negative',
        message: 'Failed to initialize form. Please refresh the page.',
        position: 'top',
      })
    }
  }
}
